/* =============================================================
   DOZO Native Cart Drawer — M2 JavaScript Controller
   M1: drawer, items, qty/remove, subtotal, reward bar, consent, checkout fix
   M2: per-rule upsells (FBT), free gift auto-add/remove, discount code carry,
       trust badges, CLAUDE-managed dozo-cart-rules.json config
   - Vanilla JS, no dependencies
   - Opens on #cart-icon-bubble click + intercepts add-to-cart forms
   - AJAX cart: /cart.js, /cart/add.js, /cart/change.js, /cart/clear.js
   - Checkout fix: immediate disabled+loading state → single /checkout navigate
   - Rules: assets/dozo-cart-rules.json, deepMerge(default, collection, product)
   ============================================================= */

(function () {
  'use strict';

  /* ── Config (overridden by data-* attrs on #dozo-cart-drawer-root) ── */
  const DEFAULT_CONFIG = {
    freeShippingThreshold: 7500,   // cents ($75)
    freeGiftThreshold:     19900,  // cents ($199)
    discountThreshold:     25000,  // cents ($250)
    discountPercent:       20,
    trustCopy:             '4.8 (342k) | Secure Checkout',
    policyPageUrl:         '/pages/policy',
    rulesUrl:              null,   // injected from data-rules-url
  };

  /* ── Money formatting ── */
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── DOM helpers ── */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ── Deep merge (rules resolution) ── */
  function deepMerge(base, override) {
    if (!override) return Object.assign({}, base);
    const result = Object.assign({}, base);
    for (const key of Object.keys(override)) {
      if (key.startsWith('_')) continue; // skip _comment fields
      if (override[key] !== null && typeof override[key] === 'object' && !Array.isArray(override[key])) {
        result[key] = deepMerge(base[key] || {}, override[key]);
      } else {
        result[key] = override[key];
      }
    }
    return result;
  }

  /* ── State ── */
  let cart = null;
  let consentChecked = true;   // pre-checked by default (Liquid renders the box checked)
  let isCheckingOut = false;
  let cfg = Object.assign({}, DEFAULT_CONFIG);
  let rules = null;          // loaded dozo-cart-rules.json
  let effectiveRule = null;  // resolved per cart context
  let activePromo = null;    // active 3+1 promo group (qty-based), null = default dollar gift
  let giftGuard = false;     // anti-loop for gift add/remove

  /* ── Elements (resolved after DOMContentLoaded) ── */
  let drawerRoot, overlay, closeBtn, itemsWrapper,
      rewardFill, rewardText, rewardMarkers,
      subtotalAmount, savingsRow, savingsAmount, consentCheckbox, checkoutBtn,
      upsellSection, giftBlock, trustSection, cartTitleCount;

  /* ── Monster Upsells shim ──────────────────────────────
     Theme assets (variant-picker.js, bundle.js) and the Zipify app call
     window.monster_refresh() right after a successful /cart/add. Monster Upsells
     is removed, so without this the global is undefined → those scripts throw and
     alert("Error adding products"). Define it so it refreshes/opens our drawer.
     Declared at IIFE top so it exists before any add-to-cart click. */
  window.monster_refresh = function (open) {
    if (!drawerRoot) return;          // drawer not initialized yet → no-op (avoids throw)
    refreshCart(open !== false);      // monster_refresh(true) → refresh + open drawer
  };

  /* ────────────────────────────────────────────
     RULES LOADING + RESOLUTION
  ──────────────────────────────────────────── */

  function loadRules() {
    const url = cfg.rulesUrl;
    if (!url) return Promise.resolve();
    return fetch(url + '?v=' + Date.now(), { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        rules = data;
      })
      .catch(function (err) {
        console.warn('[dozo-cart] rules load failed', err);
      });
  }

  function resolveRule(cartData) {
    if (!rules || !cartData || !cartData.items || cartData.items.length === 0) {
      return null;
    }

    const defaultRule = rules.default || {};
    let merged = Object.assign({}, defaultRule);

    // Context = first non-gift cart item
    const primaryItem = cartData.items.find(function (item) {
      const props = item.properties || {};
      return !props._dozo_gift;
    });
    if (!primaryItem) return merged;

    const handle = primaryItem.handle;

    // Check product rule first (wins)
    const productRule = rules.products && rules.products[handle];
    if (productRule) {
      return deepMerge(merged, productRule);
    }

    // Check collection membership (window.__dozoCollectionMembers emitted by Liquid)
    const members = window.__dozoCollectionMembers || {};
    if (rules.collections) {
      for (const collHandle of Object.keys(rules.collections)) {
        const memberList = members[collHandle] || [];
        if (memberList.includes(handle)) {
          return deepMerge(merged, rules.collections[collHandle]);
        }
      }
    }

    return merged;
  }

  /* ── Promo (3+1 / BOGO) resolution — QTY-based, scans ALL cart items ──
     rules.promos.<key> = { qty, sort_cents, handles[] }. A promo is active when
     the cart holds ≥1 PAID unit from its handle list ($0 gift lines excluded).
     Multiple promo groups in cart → the one closest to unlock wins (tie → first
     in cart). Promos take PRIORITY over the default dollar-based gift. */
  function resolvePromo(cartData) {
    if (!rules || !rules.promos || !cartData || !cartData.items || cartData.items.length === 0) return null;

    var best = null;
    Object.keys(rules.promos).forEach(function (key) {
      if (key.charAt(0) === '_') return; // skip _comment
      var p = rules.promos[key];
      if (!p || !p.handles || !p.qty) return;

      var qty = 0, firstIdx = -1;
      cartData.items.forEach(function (item, idx) {
        if (item.properties && item.properties._dozo_gift) return;                    // our own gift line
        if (item.final_line_price === 0 && item.original_line_price > 0) return;      // Appstle $0 gift line — not a paid unit
        if (p.handles.indexOf(item.handle) !== -1) {
          qty += item.quantity;
          if (firstIdx === -1) firstIdx = idx;
        }
      });
      if (qty === 0) return;

      var progress = qty / p.qty;
      if (!best || progress > best.progress || (progress === best.progress && firstIdx < best.firstIdx)) {
        best = { key: key, cfg: p, qty: qty, required: p.qty, progress: progress, firstIdx: firstIdx };
      }
    });
    return best;
  }

  /* ────────────────────────────────────────────
     INIT
  ──────────────────────────────────────────── */

  function init() {
    drawerRoot = $('#dozo-cart-drawer-root');
    overlay    = $('#dozo-cart-overlay');
    if (!drawerRoot || !overlay) return;

    // Read config from data-* attrs
    const d = drawerRoot.dataset;
    if (d.freeShipping)    cfg.freeShippingThreshold = parseInt(d.freeShipping, 10);
    if (d.freeGift)        cfg.freeGiftThreshold     = parseInt(d.freeGift, 10);
    if (d.discount)        cfg.discountThreshold      = parseInt(d.discount, 10);
    if (d.discountPercent) cfg.discountPercent        = parseInt(d.discountPercent, 10);
    if (d.trustCopy)       cfg.trustCopy              = d.trustCopy;
    if (d.policyPage)      cfg.policyPageUrl          = d.policyPage;
    if (d.rulesUrl)        cfg.rulesUrl               = d.rulesUrl;

    closeBtn        = $('#dozo-cart-close', drawerRoot);
    cartTitleCount  = $('#dozo-cart-title-count', drawerRoot);
    itemsWrapper    = $('#dozo-cart-items-wrap', drawerRoot);
    rewardFill      = $('#dozo-reward-fill', drawerRoot);
    rewardText      = $('#dozo-reward-text', drawerRoot);
    rewardMarkers   = $$('.dz-reward-bar__marker', drawerRoot);
    subtotalAmount  = $('#dozo-subtotal-amount', drawerRoot);
    savingsRow      = $('#dozo-savings-row', drawerRoot);
    savingsAmount   = $('#dozo-savings-amount', drawerRoot);
    consentCheckbox = $('#dozo-consent-checkbox', drawerRoot);
    checkoutBtn     = $('#dozo-checkout-btn', drawerRoot);
    upsellSection   = $('#dozo-upsell-section', drawerRoot);
    giftBlock       = $('#dozo-gift-block', drawerRoot);
    trustSection    = $('#dozo-trust-section', drawerRoot);

    bindEvents();
    patchAddToCartForms();
    listenForCartEvents();

    // Load rules then render trust badges
    loadRules().then(function () {
      renderTrustBadges();
    });
  }

  /* ── Event binding ── */
  function bindEvents() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openDrawer();
      }, true);
    }

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    if (consentCheckbox) {
      consentCheckbox.addEventListener('change', function () {
        consentChecked = this.checked;
        updateCheckoutBtn();
      });
    }

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', handleCheckout);
    }
  }

  /* ── Intercept add-to-cart forms ── */
  function patchAddToCartForms() {
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || form.getAttribute('action') !== '/cart/add') return;
      if (form.querySelector('[name="checkout"]')) return;

      e.preventDefault();
      const formData = new FormData(form);
      const id = formData.get('id');
      const qty = parseInt(formData.get('quantity'), 10) || 1;
      const sellingPlan = formData.get('selling_plan');

      if (!id) return;
      const item = { id: parseInt(id, 10), quantity: qty };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

      const addBtn = form.querySelector('[type="submit"]');
      if (addBtn) {
        addBtn.disabled = true;
        addBtn._origText = addBtn.textContent;
        addBtn.textContent = 'Adding...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
        body: JSON.stringify({ items: [item] }),
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          if (addBtn) { addBtn.disabled = false; addBtn.textContent = addBtn._origText || 'Add to cart'; }
          refreshCart(true);
        })
        .catch(function (err) {
          console.error('[dozo-cart] add error', err);
          if (addBtn) { addBtn.disabled = false; addBtn.textContent = addBtn._origText || 'Add to cart'; }
          showToast('Could not add item. Please try again.');
        });
    });
  }

  function listenForCartEvents() {
    document.addEventListener('cart:open', openDrawer);
    document.addEventListener('cart:refresh', function () { refreshCart(false); });

    // Bulletproof add-to-cart catch: ANY POST to /cart/add(.js) — from <product-info>
    // web components, apps, custom buttons — opens our drawer. Our own internal adds are
    // tagged X-Dozo-Internal and skipped (they already call refreshCart).
    if (!window.__dozoCartPatched) {
      window.__dozoCartPatched = true;
      var isAddUrl = function (u) { return /\/cart\/add(\.js)?(\?|$)/.test(String(u || '')); };
      var isInternal = function (opts) {
        if (!opts || !opts.headers) return false;
        var h = opts.headers;
        if (typeof h.get === 'function') return !!h.get('X-Dozo-Internal');
        return !!(h['X-Dozo-Internal'] || h['x-dozo-internal']);
      };
      var openSoon = function () { setTimeout(function () { refreshCart(true); }, 80); };
      var origFetch = window.fetch;
      if (origFetch) {
        window.fetch = function (input, opts) {
          var url = (typeof input === 'string') ? input : (input && input.url) || '';
          var method = ((opts && opts.method) || (input && input.method) || 'GET').toUpperCase();
          var watch = method === 'POST' && isAddUrl(url) && !isInternal(opts);
          var pr = origFetch.apply(this, arguments);
          if (watch && pr && pr.then) {
            pr.then(function (res) { if (res && res.ok) openSoon(); }).catch(function () {});
          }
          return pr;
        };
      }
      try {
        var origOpen = XMLHttpRequest.prototype.open;
        var origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (m, u) {
          this.__dozoAdd = String(m).toUpperCase() === 'POST' && isAddUrl(u);
          return origOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
          var xhr = this;
          if (xhr.__dozoAdd) {
            xhr.addEventListener('load', function () { if (xhr.status >= 200 && xhr.status < 300) openSoon(); });
          }
          return origSend.apply(this, arguments);
        };
      } catch (e) { /* XHR patch optional */ }
    }
  }

  /* ── Open / Close ── */
  function openDrawer() {
    drawerRoot.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    refreshCart(false);
  }

  function closeDrawer() {
    drawerRoot.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ── Fetch cart state ── */
  function refreshCart(openAfter) {
    setItemsLoading(true);
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        cart = data;
        effectiveRule = resolveRule(cart);
        activePromo = resolvePromo(cart);
        renderCart();
        updateCartIcon();
        if (openAfter) openDrawer();
        // Gift logic runs after render, skip if guard active
        if (!giftGuard) handleGiftLogic();
      })
      .catch(function (err) {
        console.error('[dozo-cart] refresh error', err);
        setItemsLoading(false);
      });
  }

  function setItemsLoading(on) {
    if (itemsWrapper) {
      itemsWrapper.style.opacity = on ? '0.5' : '1';
      itemsWrapper.style.pointerEvents = on ? 'none' : '';
    }
  }

  /* ────────────────────────────────────────────
     RENDER CART
  ──────────────────────────────────────────── */

  function renderCart() {
    setItemsLoading(false);
    if (!cart) return;

    if (cartTitleCount) {
      var nonGiftCount = (cart.items || []).reduce(function (s, i) { return (i.properties && i.properties._dozo_gift) ? s : s + i.quantity; }, 0);
      cartTitleCount.textContent = nonGiftCount > 0 ? ' (' + nonGiftCount + ')' : '';
    }

    renderRewardBar(cart.total_price);
    renderItems(cart.items);
    renderSubtotal(cart.total_price);
    updateSavings();
    renderGiftBlock(cart);
    renderUpsells(cart);
    updateCheckoutBtn();
  }

  function renderSubtotal(totalCents) {
    if (subtotalAmount) subtotalAmount.textContent = formatMoney(totalCents);
  }

  /* ── Savings row (total cart discount) ── */
  function updateSavings() {
    if (!savingsRow) return;
    var disc = (cart && cart.total_discount) || 0;
    if (disc > 0) {
      savingsRow.style.display = '';
      var base = (cart.total_price || 0) + disc;          // total before discount
      var pct = base > 0 ? Math.round((disc / base) * 100) : 0;
      if (savingsAmount) savingsAmount.textContent = '−' + formatMoney(disc) + (pct > 0 ? ' (' + pct + '%)' : '');
    } else {
      savingsRow.style.display = 'none';
    }
  }

  /* ── Appstle "Buy X Get Y" promo free gift present? (a $0 line that isn't our own gift) ── */
  function hasAppstleGift() {
    return !!(cart && cart.items && cart.items.some(function (i) {
      return i.final_line_price === 0 && i.original_line_price > 0 && !(i.properties && i.properties._dozo_gift);
    }));
  }

  /* ── Reward bar ──
     Milestones are built dynamically (2026-07-01):
     - Free Shipping — always, dollar-based ($75).
     - Free Gift — QTY-based when a 3+1 promo group is active (mirrors Appstle
       bundle: "Add N more"), else dollar-based default ($250, drawer auto-adds).
     - 20% OFF milestone REMOVED globally (user request; CART20 no longer offered).
     Ordering by sortVal (≈ dollar cost to unlock). Fill = (achieved + progress
     of next milestone) / N — shows early progress even for qty promos. */
  function renderRewardBar(totalCents) {
    if (!rewardFill || !rewardText) return;

    const rule = effectiveRule || {};
    const s = (rule.free_shipping_threshold_cents != null ? rule.free_shipping_threshold_cents : cfg.freeShippingThreshold);
    const appstleGift = hasAppstleGift();

    var milestones = [];

    // Free Shipping — dollar-based
    milestones.push({
      key: 'ship', label: 'Free Shipping', sortVal: s,
      achieved: totalCents >= s,
      progress: s > 0 ? Math.min(1, totalCents / s) : 1,
      msg: '🚚 You’re ' + formatMoney(Math.max(0, s - totalCents)) + ' away from <strong>FREE Shipping</strong>'
    });

    if (activePromo) {
      // Qty-based 3+1 promo gift — achieved at N paid units (or when Appstle gift already in cart)
      var needUnits = Math.max(0, activePromo.required - activePromo.qty);
      milestones.push({
        key: 'gift', label: 'Free Gift',
        sortVal: (activePromo.cfg.sort_cents != null ? activePromo.cfg.sort_cents : 0),
        achieved: appstleGift || activePromo.qty >= activePromo.required,
        progress: Math.min(1, activePromo.qty / activePromo.required),
        msg: '🎁 Add <strong>' + needUnits + ' more</strong> to unlock a <strong>FREE Gift</strong>'
      });
    } else if (rule.gift && rule.gift.threshold_cents) {
      // Default dollar-based gift
      var g = rule.gift.threshold_cents;
      milestones.push({
        key: 'gift', label: 'Free Gift',
        sortVal: appstleGift ? 0 : g,
        achieved: appstleGift || totalCents >= g,
        progress: g > 0 ? Math.min(1, totalCents / g) : 1,
        msg: '🎁 You’re ' + formatMoney(Math.max(0, g - totalCents)) + ' away from a <strong>FREE Gift</strong>'
      });
    }

    milestones.sort(function (a, b) { return a.sortVal - b.sortVal; });

    // Headline = next unachieved milestone (by unlock-cost order)
    var next = null;
    for (var k = 0; k < milestones.length; k++) { if (!milestones[k].achieved) { next = milestones[k]; break; } }
    var msg = next ? next.msg : '🎉 You unlocked all rewards!';

    // Fill = (fully achieved count + progress of next unachieved) / N
    var N = milestones.length, achievedCount = 0;
    for (var j = 0; j < N; j++) { if (milestones[j].achieved) achievedCount++; }
    var percent;
    if (!next) {
      percent = 100;
    } else {
      percent = Math.round(((achievedCount + Math.max(0, Math.min(1, next.progress))) / N) * 100);
    }
    rewardFill.style.width = Math.min(100, Math.max(0, percent)) + '%';
    rewardText.innerHTML = msg;

    rewardMarkers.forEach(function (marker, i) {
      var m = milestones[i];
      if (!m) { marker.style.display = 'none'; return; }
      marker.style.display = '';
      marker.innerHTML = (m.achieved ? '<span class="dz-reward-check">✓</span> ' : '') + m.label;
      marker.classList.toggle('is-unlocked', m.achieved);
    });
  }

  /* ── Line items ── */
  function renderItems(items) {
    if (!itemsWrapper) return;

    if (!items || items.length === 0) {
      itemsWrapper.innerHTML = '<div class="dz-cart__empty"><div class="dz-cart__empty-icon">🛒</div><p class="dz-cart__empty-text">Your cart is empty</p></div>';
      return;
    }

    const html = items.map(function (item, idx) { return renderItem(item, idx + 1); }).join('');
    itemsWrapper.innerHTML = '<div class="dz-cart__items">' + html + '</div>';
    bindItemHandlers();
  }

  function renderItem(item, line) {
    const isOurGift = item.properties && item.properties._dozo_gift;
    // Appstle "Buy X Get Y" free item = a $0 line that isn't ours
    const isAppstleGift = item.final_line_price === 0 && item.original_line_price > 0 && !isOurGift;
    const isGift = isOurGift || isAppstleGift;
    const imgSrc = item.image
      ? item.image.replace(/(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_120x120$1$3')
      : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_small.png';

    const price = formatMoney(item.final_line_price);
    const origPrice = item.original_line_price !== item.final_line_price ? formatMoney(item.original_line_price) : null;
    const variantParts = [];
    const vOpts = item.options_with_values || [];
    if (vOpts.length) {
      // Skip the first "offer/size" option (already in the product title) to avoid repeating the whole name
      (vOpts.length > 1 ? vOpts.slice(1) : vOpts).forEach(function (o) {
        const v = (o.value || '').trim();
        if (v && v !== 'Default Title') variantParts.push(v);
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      variantParts.push(item.variant_title);
    }

    let sellingPlanHtml = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanHtml = '<span class="dz-cart-item__selling-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</span>';
    }

    const compareHtml = origPrice ? '<span class="dz-cart-item__compare">' + origPrice + '</span>' : '';

    // Per-item savings: clean "Save $X (Y%)" computed from line prices (no raw discount-name string)
    let saveBadgeHtml = '';
    if (!isGift) {
      const saveAmt = (item.original_line_price || 0) - (item.final_line_price || 0);
      if (saveAmt > 0 && item.original_line_price > 0) {
        const savePct = Math.round((saveAmt / item.original_line_price) * 100);
        saveBadgeHtml = '<span class="dz-cart-item__save-badge">' +
          '<svg class="dz-save-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.6 1.5H3a1.5 1.5 0 00-1.5 1.5v5.6c0 .4.16.78.44 1.06l5 5a1.5 1.5 0 002.12 0l4.94-4.94a1.5 1.5 0 000-2.12l-5-5A1.5 1.5 0 008.6 1.5z" stroke="#5A75F8" stroke-width="1.3" stroke-linejoin="round"/><circle cx="5.2" cy="5.2" r="1.1" fill="#5A75F8"/></svg>' +
          'Save ' + formatMoney(saveAmt) + ' (' + savePct + '%)</span>';
      }
    }

    const giftBadge = isGift ? '<span class="dz-cart-item__gift-badge">FREE GIFT</span>' : '';
    // Gift lines have no stepper, so a multi-unit gift (e.g. 1+1 promo: 3 paid + 3 free)
    // read as a single item — show a static qty chip where the stepper normally sits.
    const giftQtyHtml = (isGift && item.quantity > 1) ? '<span class="dz-gift-qty">&times;&nbsp;' + item.quantity + '</span>' : '';
    const actionsHtml = isGift ? giftQtyHtml : (
      '<button class="dz-cart-item__trash" data-line="' + line + '" aria-label="Remove item" title="Remove">' +
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3.5h12M4.5 3.5V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5v1M5.5 6v4M8.5 6v4M2 3.5l.7 8.05A.5.5 0 003.2 12h7.6a.5.5 0 00.499-.45L12 3.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<div class="dz-qty-stepper">' +
        '<button class="dz-qty-stepper__btn dz-qty-minus" data-line="' + line + '" aria-label="Decrease quantity"' + (item.quantity <= 1 ? ' disabled' : '') + '>−</button>' +
        '<span class="dz-qty-stepper__count">' + item.quantity + '</span>' +
        '<button class="dz-qty-stepper__btn dz-qty-plus" data-line="' + line + '" aria-label="Increase quantity">+</button>' +
      '</div>'
    );

    return '<div class="dz-cart-item' + (isGift ? ' is-gift' : '') + '" data-line="' + line + '" data-variant-id="' + item.variant_id + '">' +
      '<div class="dz-cart-item__image-wrap"><img class="dz-cart-item__image" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="68" height="68"></div>' +
      '<div class="dz-cart-item__details">' +
        giftBadge +
        '<span class="dz-cart-item__title">' + escapeHtml(item.product_title) + '</span>' +
        (variantParts.length ? '<span class="dz-cart-item__variant">' + escapeHtml(variantParts.join(' \xB7 ')) + '</span>' : '') +
        sellingPlanHtml +
        saveBadgeHtml +
        '<div class="dz-cart-item__price-row">' + (isGift ? '<span class="dz-cart-item__free">FREE</span>' : '<span class="dz-cart-item__price">' + price + '</span>') + compareHtml + '</div>' +
      '</div>' +
      '<div class="dz-cart-item__actions">' + actionsHtml + '</div>' +
    '</div>';
  }

  function bindItemHandlers() {
    if (!itemsWrapper) return;
    $$('.dz-qty-minus', itemsWrapper).forEach(function (btn) {
      btn.addEventListener('click', function () { changeQty(parseInt(btn.dataset.line, 10), -1); });
    });
    $$('.dz-qty-plus', itemsWrapper).forEach(function (btn) {
      btn.addEventListener('click', function () { changeQty(parseInt(btn.dataset.line, 10), +1); });
    });
    $$('.dz-cart-item__trash', itemsWrapper).forEach(function (btn) {
      btn.addEventListener('click', function () { removeItem(parseInt(btn.dataset.line, 10)); });
    });
  }

  /* ────────────────────────────────────────────
     FREE GIFT LOGIC
  ──────────────────────────────────────────── */

  function handleGiftLogic() {
    if (!cart) return;

    // Promo (3+1) context OR Appstle gift present → the drawer NEVER auto-adds its
    // own gift (Appstle bundle owns the gift). Remove our stale gift if it slipped
    // in (e.g. context switched from a default cart to a promo cart).
    if (activePromo || hasAppstleGift()) {
      const ourGift = cart.items.find(function (i) { return i.properties && i.properties._dozo_gift; });
      if (ourGift && !giftGuard) {
        giftGuard = true;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
          body: JSON.stringify({ line: cart.items.indexOf(ourGift) + 1, quantity: 0 })
        }).then(function (r) { return r.json(); })
          .then(function () { giftGuard = false; refreshCart(false); })
          .catch(function () { giftGuard = false; });
      }
      return;
    }

    if (!effectiveRule || !effectiveRule.gift) return;

    const giftCfg = effectiveRule.gift;
    if (!giftCfg.product_handle || !giftCfg.variant_id || !giftCfg.threshold_cents) return;

    // Non-gift subtotal
    const nonGiftTotal = cart.items.reduce(function (sum, item) {
      return (item.properties && item.properties._dozo_gift) ? sum : sum + item.final_line_price;
    }, 0);

    const giftLine = cart.items.find(function (item) {
      return item.properties && item.properties._dozo_gift;
    });
    const giftInCart = !!giftLine;

    if (nonGiftTotal >= giftCfg.threshold_cents && !giftInCart) {
      giftGuard = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
        body: JSON.stringify({ items: [{ id: giftCfg.variant_id, quantity: 1, properties: { _dozo_gift: 'true' } }] })
      })
        .then(function (r) { return r.json(); })
        .then(function () { giftGuard = false; refreshCart(false); })
        .catch(function (err) { console.warn('[dozo-cart] gift add failed', err); giftGuard = false; });

    } else if (nonGiftTotal < giftCfg.threshold_cents && giftInCart) {
      const lineIdx = cart.items.indexOf(giftLine) + 1;
      giftGuard = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
        body: JSON.stringify({ line: lineIdx, quantity: 0 })
      })
        .then(function (r) { return r.json(); })
        .then(function () { giftGuard = false; refreshCart(false); })
        .catch(function (err) { console.warn('[dozo-cart] gift remove failed', err); giftGuard = false; });
    }
  }

  /* ── Gift teaser block (shown when below threshold; default carts only —
        hidden for 3+1 promo carts, the reward bar handles those) ── */
  function renderGiftBlock(cartData) {
    if (activePromo) { if (giftBlock) giftBlock.style.display = 'none'; return; }
    if (!giftBlock || !effectiveRule || !effectiveRule.gift) { if (giftBlock) giftBlock.style.display = 'none'; return; }

    const giftCfg = effectiveRule.gift;
    if (!giftCfg.threshold_cents) { giftBlock.style.display = 'none'; return; }

    const nonGiftTotal = cartData.items.reduce(function (sum, item) {
      return (item.properties && item.properties._dozo_gift) ? sum : sum + item.final_line_price;
    }, 0);

    const giftInCart = cartData.items.some(function (item) { return item.properties && item.properties._dozo_gift; });

    if (hasAppstleGift() || giftInCart || nonGiftTotal >= giftCfg.threshold_cents) { giftBlock.style.display = 'none'; return; }

    giftBlock.style.display = '';
    const remaining = formatMoney(giftCfg.threshold_cents - nonGiftTotal);
    const label = giftCfg.label || 'Today only offer';

    if (!giftBlock._productData && giftCfg.product_handle) {
      giftBlock.innerHTML = renderGiftTeaser(label, remaining, null, null);
      fetch('/products/' + giftCfg.product_handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) { giftBlock._productData = p; giftBlock.innerHTML = renderGiftTeaser(label, remaining, p, giftCfg); })
        .catch(function () { giftBlock.innerHTML = renderGiftTeaser(label, remaining, null, giftCfg); });
    } else {
      giftBlock.innerHTML = renderGiftTeaser(label, remaining, giftBlock._productData, giftCfg);
    }
  }

  function renderGiftTeaser(label, remaining, productData, giftCfg) {
    const imgSrc = productData && productData.images && productData.images[0]
      ? productData.images[0].replace(/(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_80x80$1$3') : '';
    // /products/<handle>.js returns price/compare_at_price already in CENTS — do NOT *100
    const comparePrice = productData && productData.variants && productData.variants[0]
      ? formatMoney(productData.variants[0].compare_at_price || productData.variants[0].price || 0)
      : '$59.97';

    return '<div class="dz-gift-teaser__header">Add ' + remaining + ' to unlock Free Gift 🎁</div>' +
      '<div class="dz-gift-teaser__card">' +
        (imgSrc ? '<img class="dz-gift-teaser__img" src="' + escapeHtml(imgSrc) + '" alt="Free Gift" width="60" height="60">' : '<div class="dz-gift-teaser__img-placeholder"></div>') +
        '<div class="dz-gift-teaser__info">' +
          '<span class="dz-gift-teaser__label">' + escapeHtml(label) + '</span>' +
          '<div class="dz-gift-teaser__price"><span class="dz-gift-teaser__compare">' + comparePrice + '</span> <span class="dz-gift-teaser__free">Free</span></div>' +
        '</div>' +
      '</div>';
  }

  /* ────────────────────────────────────────────
     FREQUENTLY BOUGHT TOGETHER UPSELLS
  ──────────────────────────────────────────── */

  function renderUpsells(cartData) {
    if (!upsellSection) return;

    if (!effectiveRule || !effectiveRule.upsell_handles || effectiveRule.upsell_handles.length === 0) {
      upsellSection.style.display = 'none'; return;
    }

    const cartHandles = (cartData.items || []).map(function (item) { return item.handle; });
    const maxUpsells = effectiveRule.max_upsells || 3;
    const toShow = effectiveRule.upsell_handles.filter(function (h) { return !cartHandles.includes(h); }).slice(0, maxUpsells);

    if (toShow.length === 0) { upsellSection.style.display = 'none'; return; }

    upsellSection.style.display = '';
    upsellSection.innerHTML = '<h3 class="dz-upsell__heading">Frequently Bought Together</h3>' +
      '<div class="dz-upsell__list">' + toShow.map(function () { return '<div class="dz-upsell-card dz-upsell-card--loading"></div>'; }).join('') + '</div>';

    const listEl = upsellSection.querySelector('.dz-upsell__list');

    Promise.all(toShow.map(function (handle) {
      return fetch('/products/' + handle + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    })).then(function (products) {
      const cards = products.map(function (product, i) {
        try {
          if (!product) return '';
          const handle = toShow[i];
          const variant = (product.variants || []).find(function (v) { return v.available; }) || product.variants[0];
          if (!variant) return '';
          const img = product.images && product.images[0]
            ? product.images[0].replace(/(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_100x100$1$3') : '';
          // /products/<handle>.js returns price/compare_at_price already in CENTS
          const price = formatMoney(variant.price);
          const compareAt = (variant.compare_at_price && variant.compare_at_price > variant.price) ? formatMoney(variant.compare_at_price) : null;

          return '<div class="dz-upsell-card" data-handle="' + escapeHtml(handle) + '" data-variant-id="' + variant.id + '">' +
            (img ? '<img class="dz-upsell-card__img" src="' + escapeHtml(img) + '" alt="' + escapeHtml(product.title) + '" width="72" height="72">' : '<div class="dz-upsell-card__img-placeholder"></div>') +
            '<div class="dz-upsell-card__info">' +
              '<span class="dz-upsell-card__title">' + escapeHtml(product.title) + '</span>' +
              '<div class="dz-upsell-card__price-row">' + (compareAt ? '<span class="dz-upsell-card__compare">' + compareAt + '</span> ' : '') + '<span class="dz-upsell-card__price">' + price + '</span></div>' +
              '<a class="dz-upsell-card__details" href="/products/' + escapeHtml(handle) + '" target="_blank" rel="noopener">Show Details</a>' +
            '</div>' +
            '<button class="dz-upsell-card__add-btn" data-variant-id="' + variant.id + '" aria-label="Add ' + escapeHtml(product.title) + ' to cart">Add To Cart</button>' +
          '</div>';
        } catch (e) { return ''; }
      }).filter(Boolean).join('');

      // Re-query the live list (guards against a render-race detaching the captured node)
      const liveList = upsellSection.querySelector('.dz-upsell__list');
      if (liveList && cards) { liveList.innerHTML = cards; bindUpsellHandlers(); }
      else if (!cards) { upsellSection.style.display = 'none'; }
    }).catch(function (err) {
      console.warn('[dozo-cart] upsell render failed', err);
      if (upsellSection) upsellSection.style.display = 'none';
    });
  }

  function bindUpsellHandlers() {
    if (!upsellSection) return;
    $$('.dz-upsell-card__add-btn', upsellSection).forEach(function (btn) {
      btn.addEventListener('click', function () {
        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) return;
        btn.disabled = true;
        btn.textContent = '...';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        })
          .then(function (r) { return r.json(); })
          .then(function () { refreshCart(false); })
          .catch(function (err) { console.error('[dozo-cart] upsell add error', err); btn.disabled = false; btn.textContent = 'Add'; showToast('Could not add item.'); });
      });
    });
  }

  /* ────────────────────────────────────────────
     TRUST BADGES (inline SVG, no external deps)
  ──────────────────────────────────────────── */

  function renderTrustBadges() {
    if (!trustSection) return;

    var truck = '<svg width="26" height="26" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="8" width="13" height="11" rx="1.5" stroke="#444" stroke-width="1.6"/><path d="M16 11h4l3 3v5h-7" stroke="#444" stroke-width="1.6" stroke-linejoin="round"/><circle cx="8" cy="21" r="2" stroke="#444" stroke-width="1.6"/><circle cx="19" cy="21" r="2" stroke="#444" stroke-width="1.6"/></svg>';
    var flask = '<svg width="24" height="24" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 4v6l-5 8.2A1.6 1.6 0 007.4 21h13.2a1.6 1.6 0 001.4-2.8L17 10V4" stroke="#444" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 4h8" stroke="#444" stroke-width="1.6" stroke-linecap="round"/></svg>';

    trustSection.innerHTML = '<div class="dz-trust-badges">' +
      '<div class="dz-trust-badge dz-trust-badge--brand" style="color:#1a1f71;font-size:16px">VISA</div>' +
      '<div class="dz-trust-badge dz-trust-badge--brand" style="color:#e87b00;font-size:11px">DISCOVER</div>' +
      '<div class="dz-trust-badge">' + truck + '<span class="dz-trust-badge__label">Discreet Shipping</span></div>' +
      '<div class="dz-trust-badge">' + flask + '<span class="dz-trust-badge__label">Lab Tested</span></div>' +
    '</div>';
  }

  /* ────────────────────────────────────────────
     CART MUTATIONS
  ──────────────────────────────────────────── */

  function changeQty(line, delta) {
    if (!cart || !cart.items) return;
    const item = cart.items[line - 1];
    if (!item) return;
    const newQty = item.quantity + delta;
    if (newQty < 1) { removeItem(line); return; }
    setItemLoading(line, true);
    cartChange(line, newQty);
  }

  function removeItem(line) {
    setItemLoading(line, true);
    cartChange(line, 0);
  }

  function cartChange(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Dozo-Internal': '1' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        cart = data;
        effectiveRule = resolveRule(cart);
        activePromo = resolvePromo(cart);
        renderCart();
        updateCartIcon();
        if (!giftGuard) handleGiftLogic();
      })
      .catch(function (err) {
        console.error('[dozo-cart] change error', err);
        setItemLoading(line, false);
        showToast('Could not update cart. Please try again.');
      });
  }

  function setItemLoading(line, on) {
    if (!itemsWrapper) return;
    const row = itemsWrapper.querySelector('[data-line="' + line + '"]');
    if (row) row.classList.toggle('is-loading', on);
  }

  /* ── Checkout — carries discount code when threshold met ── */
  function handleCheckout(e) {
    e.preventDefault();
    if (isCheckingOut) return;
    if (!consentChecked) {
      const consentRow = $('.dz-consent', drawerRoot);
      if (consentRow) {
        consentRow.style.outline = '2px solid #e53';
        setTimeout(function () { consentRow.style.outline = ''; }, 1200);
      }
      return;
    }

    isCheckingOut = true;
    checkoutBtn.disabled = true;
    checkoutBtn.textContent = 'Processing…';
    checkoutBtn.classList.add('is-processing');

    let checkoutUrl = '/checkout';
    if (cart && effectiveRule && effectiveRule.discount) {
      const discCfg = effectiveRule.discount;
      if (cart.total_price >= discCfg.threshold_cents && discCfg.code) {
        checkoutUrl = '/checkout?discount=' + encodeURIComponent(discCfg.code);
      }
    }

    window.location.href = checkoutUrl;
  }

  function updateCheckoutBtn() {
    if (!checkoutBtn) return;
    const hasItems = cart && cart.item_count > 0;
    checkoutBtn.disabled = !consentChecked || !hasItems;
  }

  /* ── Update cart icon bubble count ── */
  function updateCartIcon() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    if (bubble && cart) bubble.textContent = cart.item_count;
    const icon = document.getElementById('cart-icon-bubble');
    if (icon && cart) icon.dataset.cartCount = cart.item_count;
  }

  /* ── Toast notification ── */
  function showToast(msg) {
    let toast = document.getElementById('dozo-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dozo-toast';
      toast.className = 'dz-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { toast.classList.remove('is-visible'); }, 3500);
  }

  /* ── HTML escape ── */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
